import { Auth0Provider } from '@auth0/auth0-react';
import { CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import React, { useEffect } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { router } from './router';
import { queryClient } from './services/queryClient';
import { initDatadog, initFeatureFlags } from './utils/datadog';
import { initPendo } from './utils/pendo';

const domain = import.meta.env.VITE_AUTH0_DOMAIN || '';
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
const audience = import.meta.env.VITE_AUTH0_AUDIENCE || '';
const issuer = import.meta.env.VITE_AUTH0_ISSUER || `https://${domain}/`;

const AppContent: React.FC = () => {
  const {
    hasPermission,
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    logout,
    whenReady,
  } = useAuth();

  return (
    <RouterProvider
      router={router}
      context={{
        queryClient,
        auth: {
          isAuthenticated,
          isLoading,
          hasPermission,
          whenReady,
          login: (returnTo?: string) =>
            loginWithRedirect({
              appState: { returnTo: returnTo ?? window.location.href },
            }),
          logout: () =>
            logout({ logoutParams: { returnTo: window.location.origin } }),
        },
      }}
    />
  );
};

const App: React.FC = () => {
  useEffect(() => {
    initDatadog();
    initFeatureFlags();
    initPendo();
  }, []);

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      issuer={issuer}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      authorizationParams={{
        audience,
        redirect_uri: window.location.origin,
        screen_hint: 'login',
      }}
    >
      <AuthProvider>
        <SnackbarProvider>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <CssBaseline />
              <AppContent />
            </ThemeProvider>
          </QueryClientProvider>
        </SnackbarProvider>
      </AuthProvider>
    </Auth0Provider>
  );
};

export default App;
